import type { ArbiterRole } from "components/Screens/Marketplace/Customer/types";
import type { FilterOption } from "components/Screens/Marketplace/Customer/TalentNetwork/useTalentFilterOptions";

// Industry keys from marketplace roles (talentWorkPreferences industries), in the order the filter lists them.
const INDUSTRY_ORDER = ["medical", "dental", "insurance", "finance", "business_administration"];
const INDUSTRY_LABELS: Record<string, string> = {
  medical: "Medical",
  dental: "Dental",
  insurance: "Insurance",
  finance: "Finance",
  business_administration: "Business Administration",
};
const NO_INDUSTRY = "Other";

const industryKey = (role: ArbiterRole) => (role.industry || "").trim().toLowerCase();

/** "medical" → "Medical"; an unknown key is title-cased ("home_services" → "Home Services"); none → "Other". */
export function industryLabel(key: string): string {
  if (!key) return NO_INDUSTRY;
  return INDUSTRY_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Known industries in their order, then any other industry alphabetically, then roles with no industry.
const industryRank = (key: string) => {
  const i = INDUSTRY_ORDER.indexOf(key);
  if (i >= 0) return i;
  return key ? INDUSTRY_ORDER.length : INDUSTRY_ORDER.length + 1;
};

/**
 * Role filter options from the arbiter roles: title + shortDescription, grouped under an industry heading
 * (Medical, Dental, ...), each group in the roles' sortOrder then by title. Inactive roles are left out,
 * and a title listed under two industries is offered once, under the first.
 */
export function roleOptions(roles: ArbiterRole[]): FilterOption[] {
  const seen = new Set<string>();
  return roles
    .filter((r) => typeof r?.title === "string" && r.title.trim() && r.isActive !== false)
    .sort((a, b) => {
      const ka = industryKey(a);
      const kb = industryKey(b);
      return (
        industryRank(ka) - industryRank(kb) ||
        ka.localeCompare(kb) ||
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.title.localeCompare(b.title)
      );
    })
    .filter((r) => {
      const k = r.title.trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .map((r) => {
      const title = r.title.trim();
      return {
        value: title,
        label: title,
        group: industryLabel(industryKey(r)),
        // The short line written for the filter (product 2026-10-26); the long description is not shown
        ...(r.shortDescription?.trim() ? { description: r.shortDescription.trim() } : {}),
      };
    });
}
